import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  T, serif, Card, Chip, Boton, Campo, estiloInput,
  Titulo, Aviso, Vacio, Cargando,
} from '../lib/ui';
import { mensajeDeError } from './Gastos';

// ════════════════════════════════════════════════════════════════════
// Padrón de personal
//
// La lista de trabajadores que reciben recibos y aparecen en las
// planillas de viáticos. Aparte de los usuarios de la plataforma: la
// mayoría del personal de obra no entra a la aplicación.
//
// Quien se va se da de BAJA, no se borra: sus documentos anteriores
// seguirían apuntando a él.
// ════════════════════════════════════════════════════════════════════

export function Personal({ usuario }) {
  const [lista, setLista] = useState(null);
  const [verBajas, setVerBajas] = useState(false);
  const [busca, setBusca] = useState('');
  const [modo, setModo] = useState(null);      // 'nuevo' | 'importar'
  const [editando, setEditando] = useState(null);
  const [aviso, setAviso] = useState(null);

  const administra = ['mando_medio', 'contabilidad', 'director'].includes(usuario.rol);

  const cargar = () => {
    api.personal({ todos: verBajas ? '1' : '' })
      .then((r) => setLista(r.personal || []))
      .catch((err) => { setAviso({ tipo: 'bad', texto: mensajeDeError(err) }); setLista([]); });
  };

  useEffect(() => { cargar(); }, [verBajas]);   // eslint-disable-line

  if (!lista) return <Cargando />;

  const filtrada = busca.trim()
    ? lista.filter((p) =>
        p.nombre.toLowerCase().includes(busca.toLowerCase()) || p.dni.includes(busca))
    : lista;

  const activos = lista.filter((p) => p.activo).length;

  const darBaja = async (p) => {
    const motivo = window.prompt(
      `Dar de baja a ${p.nombre}.\n\n` +
      'Dejará de aparecer al elegir personal, pero sus recibos y planillas ' +
      'anteriores se conservan intactos.\n\nMotivo:',
    );
    if (motivo === null) return;

    try {
      await api.bajaPersonal(p.id, motivo.trim());
      setAviso({ tipo: 'ok', texto: `${p.nombre} dado de baja.` });
      cargar();
    } catch {
      setAviso({ tipo: 'bad', texto: 'No se pudo dar de baja.' });
    }
  };

  const reincorporar = async (p) => {
    try {
      await api.reincorporarPersonal(p.id);
      setAviso({ tipo: 'ok', texto: `${p.nombre} reincorporado.` });
      cargar();
    } catch {
      setAviso({ tipo: 'bad', texto: 'No se pudo reincorporar.' });
    }
  };

  const borrar = async (p) => {
    let uso;
    try { uso = await api.usoPersonal(p.id); }
    catch { return setAviso({ tipo: 'bad', texto: 'No se pudo comprobar el uso.' }); }

    if (!uso.sePuedeBorrar) {
      return setAviso({
        tipo: 'warn',
        texto: `${p.nombre} tiene ${uso.gastos} documento(s) y ${uso.viaticos} planilla(s). `
             + 'Dale de baja en lugar de borrarlo, así el historial queda intacto.',
      });
    }
    if (!window.confirm(`¿Borrar a ${p.nombre} definitivamente?\n\nNunca se usó, así que no queda rastro.`)) return;

    try {
      await api.borrarPersonal(p.id);
      setAviso({ tipo: 'ok', texto: `${p.nombre} eliminado.` });
      cargar();
    } catch {
      setAviso({ tipo: 'bad', texto: 'No se pudo eliminar.' });
    }
  };

  return (
    <>
      <Titulo sub="Trabajadores que reciben recibos y aparecen en las planillas">
        Personal
      </Titulo>

      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      {administra && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <Boton variante={modo === 'nuevo' ? 'suave' : 'acento'}
                 onClick={() => setModo(modo === 'nuevo' ? null : 'nuevo')}>
            {modo === 'nuevo' ? 'Cancelar' : 'Agregar persona'}
          </Boton>
          <Boton variante="suave"
                 onClick={() => setModo(modo === 'importar' ? null : 'importar')}>
            {modo === 'importar' ? 'Cancelar' : 'Importar desde Excel'}
          </Boton>
        </div>
      )}

      {modo === 'nuevo' && (
        <FormPersona
          onListo={() => {
            setModo(null);
            setAviso({ tipo: 'ok', texto: 'Persona agregada.' });
            cargar();
          }}
          onError={(m) => setAviso({ tipo: 'bad', texto: m })}
        />
      )}

      {modo === 'importar' && (
        <Importar
          onListo={(r) => {
            setModo(null);
            setAviso({
              tipo: r.errores.length ? 'warn' : 'ok',
              texto: `${r.creados} nuevo(s), ${r.actualizados} actualizado(s)`
                   + (r.errores.length ? `, ${r.errores.length} con problema.` : '.'),
            });
            cargar();
          }}
          onError={(m) => setAviso({ tipo: 'bad', texto: m })}
        />
      )}

      <Card style={{ marginBottom: 14, padding: 13 }}>
        <input
          value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nombre o DNI"
          style={estiloInput}
        />
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginTop: 10, gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 12, color: T.muted }}>
            {activos} activo(s){lista.length > activos ? ` · ${lista.length - activos} de baja` : ''}
          </span>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 7,
            fontSize: 12, color: T.muted, cursor: 'pointer',
          }}>
            <input type="checkbox" checked={verBajas}
                   onChange={(e) => setVerBajas(e.target.checked)} />
            Mostrar los dados de baja
          </label>
        </div>
      </Card>

      {filtrada.length === 0 ? (
        <Vacio>
          {lista.length === 0
            ? 'Todavía no hay personal registrado. Puedes pegarlo desde tu Excel.'
            : 'Nadie coincide con la búsqueda.'}
        </Vacio>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {filtrada.map((p) => (
            <Card key={p.id} style={{ padding: 13, opacity: p.activo ? 1 : 0.55 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {p.nombre}
                    {!p.activo && <span style={{ marginLeft: 8 }}><Chip estado="rechazado">De baja</Chip></span>}
                  </div>
                  <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>
                    DNI {p.dni}
                    {p.cargo ? ` · ${p.cargo}` : ''}
                    {p.centro ? ` · ${p.centro}` : ''}
                  </div>
                  {p.banco && (
                    <div style={{ color: T.muted, fontSize: 11, marginTop: 2 }}>
                      {p.banco} {p.cuenta || ''}
                    </div>
                  )}
                  {p.bajaMotivo && (
                    <div style={{ color: T.bad, fontSize: 11, marginTop: 3 }}>
                      Baja: {p.bajaMotivo}
                    </div>
                  )}
                </div>
              </div>

              {administra && (
                <div style={{ display: 'flex', gap: 7, marginTop: 11, flexWrap: 'wrap' }}>
                  <Boton variante="suave" style={btnChico}
                         onClick={() => setEditando(editando === p.id ? null : p.id)}>
                    {editando === p.id ? 'Cerrar' : 'Editar'}
                  </Boton>
                  {p.activo ? (
                    <Boton variante="suave" style={btnChico} onClick={() => darBaja(p)}>
                      Dar de baja
                    </Boton>
                  ) : (
                    <>
                      <Boton variante="suave" style={btnChico} onClick={() => reincorporar(p)}>
                        Reincorporar
                      </Boton>
                      <Boton variante="suave" style={btnChico} onClick={() => borrar(p)}>
                        Borrar
                      </Boton>
                    </>
                  )}
                </div>
              )}

              {editando === p.id && (
                <FormPersona
                  persona={p}
                  onListo={() => {
                    setEditando(null);
                    setAviso({ tipo: 'ok', texto: 'Datos actualizados.' });
                    cargar();
                  }}
                  onError={(m) => setAviso({ tipo: 'bad', texto: m })}
                />
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

const btnChico = { fontSize: 12, padding: '5px 11px' };

// ════════════════════════════════════════════════════════════════════
function FormPersona({ persona, onListo, onError }) {
  const [f, setF] = useState({
    dni: persona?.dni || '', nombre: persona?.nombre || '',
    cargo: persona?.cargo || '', area: persona?.area || '',
    centro: persona?.centro || '', banco: persona?.banco || '',
    cuenta: persona?.cuenta || '', telefono: persona?.telefono || '',
  });
  const [ocupado, setOcupado] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const guardar = async () => {
    if (f.dni.length < 7 || f.nombre.trim().length < 3) {
      return onError('Hacen falta el DNI y el nombre completo.');
    }
    setOcupado(true);
    try {
      if (persona) await api.editarPersonal(persona.id, f);
      else await api.crearPersonal(f);
      onListo();
    } catch (err) {
      onError(err.message === 'dni_repetido'
        ? 'Ya hay alguien registrado con ese DNI.'
        : 'No se pudo guardar.');
      setOcupado(false);
    }
  };

  return (
    <div style={persona
      ? { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }
      : {}}>
      {!persona && (
        <Card style={{ marginBottom: 14 }}>
          <Campos f={f} set={set} />
          <Boton onClick={guardar} disabled={ocupado} style={{ width: '100%' }}>
            {ocupado ? 'Guardando…' : 'Agregar'}
          </Boton>
        </Card>
      )}
      {persona && (
        <>
          <Campos f={f} set={set} />
          <Boton onClick={guardar} disabled={ocupado} style={{ width: '100%' }}>
            {ocupado ? 'Guardando…' : 'Guardar cambios'}
          </Boton>
        </>
      )}
    </div>
  );
}

function Campos({ f, set }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', columnGap: 12 }}>
        <Campo etiqueta="DNI">
          <input value={f.dni} inputMode="numeric" maxLength={15}
                 onChange={(e) => set('dni', e.target.value.replace(/\D/g, ''))}
                 style={estiloInput} />
        </Campo>
        <Campo etiqueta="Apellidos y nombres">
          <input value={f.nombre} onChange={(e) => set('nombre', e.target.value)}
                 style={estiloInput} />
        </Campo>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 12 }}>
        <Campo etiqueta="Cargo">
          <input value={f.cargo} onChange={(e) => set('cargo', e.target.value)}
                 style={estiloInput} />
        </Campo>
        <Campo etiqueta="Área">
          <input value={f.area} onChange={(e) => set('area', e.target.value)}
                 style={estiloInput} />
        </Campo>
        <Campo etiqueta="Centro">
          <input value={f.centro} onChange={(e) => set('centro', e.target.value.toUpperCase())}
                 style={estiloInput} />
        </Campo>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', columnGap: 12 }}>
        <Campo etiqueta="Banco">
          <input value={f.banco} onChange={(e) => set('banco', e.target.value)}
                 placeholder="BCP" style={estiloInput} />
        </Campo>
        <Campo etiqueta="Cuenta" ayuda="Para el depósito de viáticos.">
          <input value={f.cuenta} onChange={(e) => set('cuenta', e.target.value)}
                 style={estiloInput} />
        </Campo>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
function Importar({ onListo, onError }) {
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [errores, setErrores] = useState([]);

  const importar = async () => {
    if (!texto.trim()) return onError('Pega primero los datos.');
    setOcupado(true);
    setErrores([]);
    try {
      const r = await api.importarPersonal(texto);
      setErrores(r.errores || []);
      if ((r.errores || []).length === 0) onListo(r);
      else { setOcupado(false); onListo(r); }
    } catch (err) {
      onError(mensajeDeError(err));
      setOcupado(false);
    }
  };

  const filas = texto.trim() ? texto.trim().split(/\r?\n/).length : 0;

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{
        fontFamily: serif, fontSize: 17, color: T.navy,
        fontWeight: 600, marginBottom: 6,
      }}>
        Importar desde Excel
      </div>

      <div style={{ fontSize: 13, color: T.text, marginBottom: 12, lineHeight: 1.55 }}>
        Abre tu Excel, selecciona las filas del personal, cópialas con Ctrl+C y
        pégalas aquí abajo. No hace falta guardar como CSV ni subir archivo.
      </div>

      <div style={{
        background: T.warm, borderRadius: 8, padding: 12,
        marginBottom: 12, fontSize: 12,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Orden de las columnas</div>
        <div style={{ fontFamily: 'monospace', fontSize: 11, lineHeight: 1.8 }}>
          DNI · Nombre · Cargo · Área · Centro · Banco · Cuenta · Teléfono
        </div>
        <div style={{ color: T.muted, marginTop: 8 }}>
          Solo el DNI y el nombre son obligatorios; el resto puede ir vacío. Si la
          primera fila son títulos, se detecta y se salta.
        </div>
      </div>

      <textarea
        value={texto} onChange={(e) => setTexto(e.target.value)}
        rows={9}
        placeholder={'45678912\tBARRIENTOS GALDOS CRISTIAN\tTécnico\tOperaciones\tNEXA\tBCP\t191-1234567-0-11\n45678913\tFARFAN ROJAS ALEXANDER\tAyudante\tOperaciones\tNEXA'}
        style={{
          ...estiloInput, fontFamily: 'monospace', fontSize: 12,
          resize: 'vertical', whiteSpace: 'pre', overflowX: 'auto',
        }}
      />

      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginTop: 10, gap: 10,
      }}>
        <span style={{ fontSize: 12, color: T.muted }}>
          {filas > 0 ? `${filas} fila(s) pegada(s)` : 'Nada pegado todavía'}
        </span>
        <Boton variante="acento" onClick={importar} disabled={ocupado || filas === 0}>
          {ocupado ? 'Importando…' : 'Importar'}
        </Boton>
      </div>

      <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>
        Si un DNI ya existe, se actualizan sus datos en lugar de duplicarlo. Puedes
        volver a pegar la lista completa cada vez que cambie.
      </div>

      {errores.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Aviso tipo="warn">
            {errores.length} fila(s) no se pudieron importar:
          </Aviso>
          <div style={{ display: 'grid', gap: 5, marginTop: 8 }}>
            {errores.slice(0, 12).map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: T.text }}>
                Fila {e.fila}: {e.motivo}
                {e.dato ? ` — "${e.dato}"` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
